import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { LegacyPageEvent } from '@angular/material/legacy-paginator';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColumnApi,
  GridReadyEvent,
  PaginationChangedEvent,
} from 'ag-grid-community';
import { ColDef, GridApi, GridOptions } from 'ag-grid-enterprise';

import { PAGE_SIZE_OPTIONS } from '@app/core/_config/pagination.config';
import { PaginationInterface } from '@app/interfaces/pagination.model';

import {
  CostLayerChangesLogColDefs,
  COST_LAYER_CHANGES_LOG_DEFAULT_GRID_OPTIONS,
} from '../../config/cost-layer-changes-log.config';
import { ECostLayerChangesLogLayout } from '../../cost-layers.const';
import { ICostLayerChange } from '../../cost-layers.types';

type _IPaging = Pick<
  PaginationInterface,
  'current_page' | 'last_page' | 'per_page' | 'total'
>;

export class CostLayerChangesDumbLogComponentInputError extends Error {
  name = 'CostLayerChangesDumbLogComponentInputError';
}

@Component({
  selector: 'lum-cost-layer-changes-dumb-log',
  templateUrl: './cost-layer-changes-dumb-log.component.html',
  styleUrls: ['./cost-layer-changes-dumb-log.component.scss'],
})
export class CostLayerChangesDumbLogComponent implements OnChanges {
  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS;

  _paging :_IPaging = null;

  agColumnApi :ColumnApi;
  agGridApi :GridApi;
  columnDefs :ColDef[] =
    CostLayerChangesLogColDefs[ECostLayerChangesLogLayout.SALES];

  @ViewChild(AgGridAngular, { read: ElementRef }) $grid! :ElementRef;

  /** Takes priority over `columnsPreset`. */
  @Input() agGridColumns :ColDef[] | null;
  /** Overwrites _all_ defaults without merging. */
  @Input() agGridOptions :GridOptions = COST_LAYER_CHANGES_LOG_DEFAULT_GRID_OPTIONS;
  @Input() changes :ICostLayerChange[];
  @Input() columnsPreset :ECostLayerChangesLogLayout | null;
  @Input() loading :boolean = false;
  @Input() paging :PaginationInterface | 'serverSide';

  /** Pass-through event from <ag-grid-angular> */
  @Output() gridReady = new EventEmitter<GridReadyEvent>();
  /** Pass-through event from <ag-grid-angular> */
  @Output() paginationChanged = new EventEmitter<PaginationChangedEvent>();

  constructor(
    private _cd :ChangeDetectorRef,
    private _zone :NgZone,
  ) {}

  ngOnChanges(changes :SimpleChanges) {
    if (changes.agGridColumns) {
      this.#updateColumnDefs();
    } else if (changes.columnsPreset) {
      if (this.agGridColumns) {
        console.warn(
          `Ignoring 'columnsPreset' input because 'agGridColumns' input is set`,
        );
      } else {
        this.#updateColumnDefs();
      }
    }

    if (changes.loading) {
      if (this.loading) {
        this.agGridApi?.showLoadingOverlay();
      } else {
        this.agGridApi?.hideOverlay();
      }
    }

    if (changes.paging) {
      if (this.paging === 'serverSide') {
        this.#updatePagingFromAgGrid();
      } else {
        this._paging = this.paging;
      }
    }
  }

  handleGridReady(e :GridReadyEvent) {
    this.agGridApi = e.api;
    this.agColumnApi = e.columnApi;

    this.gridReady.emit(e); // pass through

    if (this.paging === 'serverSide') {
      this.#updatePagingFromAgGrid();
    }
  }

  handleModelUpdated() {
    this.#updateCalculatedHeight();

    if (this.paging === 'serverSide') {
      this.#updatePagingFromAgGrid();
    }
  }

  handlePage(e :LegacyPageEvent) {
    const { pageIndex, pageSize } = e;

    this.agGridApi?.paginationGoToPage(pageIndex);
    this.agGridApi?.paginationSetPageSize(pageSize);

    this._paging.current_page = pageIndex + 1;
    this._paging.per_page = pageSize;

    this._cd.markForCheck();
  }

  handlePaginationChanged(e :PaginationChangedEvent) {
    this.#updateCalculatedHeight();

    this.paginationChanged.emit(e); // pass through
  }

  #updateCalculatedHeight() {
    // do nothing if ag grid is already handling it
    if (this.agGridOptions?.domLayout === 'autoHeight') {
      return;
    }

    this._zone.runOutsideAngular(() => {
      let height :number = 2; // add 2 extra px, for borders

      const $els = this.$grid.nativeElement.querySelectorAll(
        '.ag-header, .ag-center-cols-container',
      );

      $els.forEach(($el :HTMLElement) => (height += $el.offsetHeight));

      this.$grid.nativeElement.style.setProperty(
        '--log-table-computed-height',
        `${height}px`,
      );
    });
  }

  #updateColumnDefs() {
    this.columnDefs =
      this.agGridColumns ??
      CostLayerChangesLogColDefs[this.columnsPreset] ??
      CostLayerChangesLogColDefs[ECostLayerChangesLogLayout.SALES];
  }

  #updatePagingFromAgGrid() {
    if (!this.agGridApi) {
      return;
    }

    this._paging = {
      current_page: this.agGridApi.paginationGetCurrentPage() + 1,
      last_page: this.agGridApi.paginationGetTotalPages(),
      per_page: this.agGridApi.paginationGetPageSize(),
      total: this.agGridApi.paginationGetRowCount(),
    };

    this._cd.markForCheck();
  }
}
